<template>
  <v-container class="inventur-container">
    <!-- Scanner-Modus: Palette ist offen -->
    <div v-if="scannerAktiv && selectedPalette">
      <v-card class="status-overview-card mb-6">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Inventur: {{ selectedPalette.paletten_nr }}</span>
          <div class="d-flex ga-2">
            <v-btn color="warning" variant="outlined" @click="deaktiviereScanner">
              <v-icon left>mdi-close</v-icon>
              Scannen beenden
            </v-btn>
            <v-btn
              color="success"
              variant="outlined"
              :disabled="selectedPalette.status !== 'erfassung'"
              @click="closeSelectedPalette"
            >
              <v-icon left>mdi-check</v-icon>
              Palette abschließen
            </v-btn>
          </div>
        </v-card-title>

        <v-card-text>
          <!-- Palette-Info -->
          <div class="info-box mb-4">
            <div class="d-flex justify-space-between">
              <div>
                <div class="info-label">Paletten-Nr.</div>
                <div class="info-value">{{ selectedPalette.paletten_nr }}</div>
              </div>
              <div>
                <div class="info-label">Status</div>
                <div class="info-value">{{ getStatusText(selectedPalette.status) }}</div>
              </div>
              <div>
                <div class="info-label">Erstellt von</div>
                <div class="info-value">{{ selectedPalette.erstellt_von_name }}</div>
              </div>
              <div>
                <div class="info-label">Positionen</div>
                <div class="info-value">{{ positionen.length }}</div>
              </div>
            </div>
          </div>

          <!-- Hinweis bei abgeschlossener Palette -->
          <v-alert
            v-if="selectedPalette && selectedPalette.status !== 'erfassung'"
            type="warning"
            variant="tonal"
            class="mb-4"
            density="compact"
          >
            <strong>Diese Palette ist abgeschlossen.</strong> Sie kann nicht mehr bearbeitet werden. Office-User können sie über die Inventur-Auswertung wieder zur Bearbeitung freigeben.
          </v-alert>

          <!-- Scanner-Input -->
          <v-row class="mb-4">
            <v-col cols="12" md="7">
              <v-text-field
                ref="scannerInput"
                v-model="scanInput"
                label="EAN/CMMF-Code scannen"
                placeholder="Barcode hier einscannen..."
                autofocus
                dense
                persistent-placeholder
                :disabled="selectedPalette && selectedPalette.status !== 'erfassung'"
                @keyup.enter="handleScan"
                class="form-field"
              />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field
                v-model.number="scanMenge"
                label="Menge"
                type="number"
                min="1"
                dense
                :disabled="selectedPalette && selectedPalette.status !== 'erfassung'"
                class="form-field"
              />
            </v-col>
            <v-col cols="3" md="1.5">
              <v-btn color="success" block @click="handleScan" :loading="loading.scanning" :disabled="selectedPalette && selectedPalette.status !== 'erfassung'">
                Scannen
              </v-btn>
            </v-col>
            <v-col cols="3" md="1.5">
              <v-btn
                color="info"
                variant="tonal"
                block
                :disabled="selectedPalette && selectedPalette.status !== 'erfassung'"
                @click="openManualSearch"
              >
                <v-icon start>mdi-magnify</v-icon>
                Suche
              </v-btn>
            </v-col>
          </v-row>

          <!-- Scan-Feedback -->
          <v-alert
            v-if="scanFeedback.show"
            :type="scanFeedback.type"
            closable
            @click:close="scanFeedback.show = false"
            class="mb-4"
          >
            {{ scanFeedback.message }}
          </v-alert>

          <!-- Positionen-Tabelle -->
          <v-data-table
            :headers="positionenTableHeaders"
            :items="positionenMitNr"
            class="mb-4"
            density="compact"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon
                size="small"
                color="error"
                @click="removePosition(item.id)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <!-- Gesamtübersicht (pro Palette nur Zählstände – Soll/Differenz erfolgt im Office, da Artikel auf mehrere Paletten verteilt sein können) -->
          <v-row>
            <v-col cols="12" md="6">
              <v-card class="info-box" variant="outlined">
                <v-card-text class="text-center">
                  <div class="info-label">Artikel</div>
                  <div class="info-value">{{ positionen.length }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="6">
              <v-card class="info-box" variant="outlined">
                <v-card-text class="text-center">
                  <div class="info-label">Stückzahl (IST)</div>
                  <div class="info-value">{{ gesamtStueckzahl }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </div>

    <!-- Übersichts-Modus: Paletten-Liste -->
    <div v-else>
      <v-card class="status-overview-card mb-6">
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Inventur</span>
          <v-btn color="success" @click="openCreatePaletteDialog">
            <v-icon left>mdi-plus</v-icon>
            Neue Inventur-Palette
          </v-btn>
        </v-card-title>

        <v-card-text>
          <!-- Statistik -->
          <v-row class="mb-6">
            <v-col cols="12" md="3">
              <v-card class="overview-card">
                <v-card-text class="text-center">
                  <div class="info-label">Offene Paletten</div>
                  <div class="info-value">{{ statistik.offene_paletten }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card class="overview-card">
                <v-card-text class="text-center">
                  <div class="info-label">Abgeschlossen</div>
                  <div class="info-value">{{ statistik.abgeschlossene_paletten }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card class="overview-card">
                <v-card-text class="text-center">
                  <div class="info-label">Gesamt Positionen</div>
                  <div class="info-value">{{ statistik.gesamt_positionen }}</div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="3">
              <v-card class="overview-card">
                <v-card-text class="text-center">
                  <div class="info-label">Gesamt IST-Bestand</div>
                  <div class="info-value">{{ statistik.gesamt_istbestand }}</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Filter und Suche -->
          <v-row class="mb-4">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="palettenSuche"
                label="Suche..."
                prepend-icon="mdi-magnify"
                class="form-field"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="statusFilter"
                :items="[
                  { title: 'Alle', value: 'alle' },
                  { title: 'Erfassung', value: 'erfassung' },
                  { title: 'Abgeschlossen', value: 'abgeschlossen' }
                ]"
                label="Status"
                class="form-field"
              />
            </v-col>
          </v-row>

          <!-- Paletten-Tabelle -->
          <v-data-table
            :headers="palettenTableHeaders"
            :items="filteredPaletten"
            :loading="loading.paletten"
            class="mb-4"
          >
            <template v-slot:[`item.status`]="{ item }">
              <v-chip :color="getStatusColor(item.status)">
                {{ getStatusText(item.status) }}
              </v-chip>
            </template>

            <template v-slot:[`item.erfassung_datum`]="{ item }">
              {{ formatDateTime(item.erfassung_datum) }}
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-btn
                icon
                size="small"
                color="primary"
                @click="loadPalette(item.id)"
                title="Öffnen"
              >
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                icon
                size="small"
                color="info"
                class="ml-1"
                @click="openPalettenscheinDialog(item)"
                title="Palettenschein drucken"
              >
                <v-icon>mdi-printer</v-icon>
              </v-btn>
              <v-btn
                v-if="canDeletePalette(item)"
                icon
                size="small"
                color="error"
                class="ml-1"
                :loading="deletingPaletteId === item.id"
                @click="deleteEmptyPalette(item)"
                title="Leere Palette löschen"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <!-- Dialog: Artikel manuell suchen -->
    <v-dialog v-model="dialogs.manualSearch" max-width="720" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-magnify</v-icon>
          Artikel manuell suchen
          <v-spacer />
          <v-btn icon variant="text" @click="dialogs.manualSearch = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3">
            Wenn der EAN-/CMMF-Code <strong>nicht scannbar oder nicht vorhanden</strong> ist,
            kann der Artikel hier nach Kurztext, CMMF oder EAN gesucht und ausgewählt werden.
          </p>
          <v-autocomplete
            v-model="manualSearch.selected"
            :items="artikelListe"
            :item-title="manualSearchItemTitle"
            item-value="id"
            return-object
            label="Artikel suchen (Kurztext, CMMF, EAN)"
            :custom-filter="manualSearchFilter"
            clearable
            autofocus
            hide-no-data
            density="comfortable"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props" :title="item.raw.kurztext || '(ohne Kurztext)'">
                <template v-slot:subtitle>
                  CMMF: {{ item.raw.cmmf_nummer || '–' }}
                  · EAN: {{ item.raw.gtin_ean || '–' }}
                  · Soll: {{ item.raw.sollbestand ?? '–' }}
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <v-text-field
            v-model.number="manualSearch.menge"
            label="Menge"
            type="number"
            min="1"
            class="mt-2"
          />

          <v-alert
            v-if="manualSearch.selected && !manualSearch.selected.gtin_ean"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-2"
            icon="mdi-barcode-off"
          >
            Hinweis: Für diesen Artikel ist noch <strong>kein EAN-Code</strong> hinterlegt.
            Das Nachpflegen von EAN-Codes erfolgt zentral im Office unter
            <em>Artikel-Import</em>.
          </v-alert>

          <v-alert
            v-if="manualSearch.selected"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-2"
          >
            <strong>{{ manualSearch.selected.kurztext || '(ohne Kurztext)' }}</strong><br>
            CMMF: {{ manualSearch.selected.cmmf_nummer || '–' }}
            · EAN: {{ manualSearch.selected.gtin_ean || '–' }}
            · Soll: {{ manualSearch.selected.sollbestand ?? '–' }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogs.manualSearch = false">Abbrechen</v-btn>
          <v-btn
            color="success"
            :disabled="!manualSearch.selected || !manualSearch.menge || manualSearch.menge < 1"
            :loading="loading.scanning"
            @click="addManualArticle"
          >
            <v-icon start>mdi-plus</v-icon>
            Hinzufügen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Mehrere Treffer für EAN/CMMF -->
    <v-dialog v-model="dialogs.scanMultiHit" max-width="900" persistent scrollable>
      <v-card>
        <v-card-title class="d-flex align-center" style="gap:8px;">
          <v-icon color="warning">mdi-alert</v-icon>
          Mehrere Artikel für „{{ scanMultiHit.ean }}"
        </v-card-title>
        <v-card-text>
          <v-alert type="info" variant="tonal" density="compact" class="mb-3">
            Es gibt {{ scanMultiHit.artikel.length }} Katalog-Einträge mit diesem Code.
            Bitte den korrekten Artikel wählen – Menge: <strong>{{ scanMultiHit.menge }}</strong> Stk.
          </v-alert>
          <v-table density="compact">
            <thead>
              <tr>
                <th>CMMF</th>
                <th>EAN</th>
                <th>Kurztext</th>
                <th>Werk</th>
                <th>Lagerort</th>
                <th class="text-end">Soll</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="a in scanMultiHit.artikel" :key="a.id">
                <td><strong>{{ a.cmmf_nummer }}</strong></td>
                <td>{{ a.gtin_ean || '–' }}</td>
                <td>{{ a.kurztext }}</td>
                <td>{{ a.werk || '–' }}</td>
                <td>{{ a.lagerort || '–' }}</td>
                <td class="text-end">{{ a.sollbestand }}</td>
                <td class="text-end">
                  <v-btn
                    size="small"
                    color="success"
                    :loading="loading.scanning"
                    @click="confirmScanMultiHit(a)"
                  >
                    <v-icon start>mdi-check</v-icon>
                    Auswählen
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="cancelScanMultiHit" :disabled="loading.scanning">
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Artikel nicht gefunden (prominenter Hinweis) -->
    <v-dialog v-model="dialogs.eanNotFound" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center bg-error text-white" style="gap:8px;">
          <v-icon>mdi-barcode-off</v-icon>
          <span>Artikel nicht gefunden</span>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert
            type="error"
            variant="tonal"
            density="comfortable"
            icon="mdi-alert-octagon-outline"
            class="mb-3"
          >
            <div class="text-h6 mb-1">Kein Artikel zum gescannten Code</div>
            <div>
              Der Code
              <strong class="text-mono">{{ notFoundEan || '—' }}</strong>
              ist im Inventur-Katalog <u>nicht hinterlegt</u>.
            </div>
          </v-alert>
          <p class="text-body-2 mb-0">
            Bitte den Artikel manuell über
            <strong>„Suche"</strong> hinzufügen oder den Code im Office unter
            <em>Artikel-Import</em> nachpflegen.
            Der Artikel wurde <strong>nicht</strong> auf die Palette gebucht.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            variant="elevated"
            ref="eanNotFoundBtn"
            @click="closeEanNotFound"
          >
            <v-icon start>mdi-barcode-scan</v-icon>
            Verstanden – weiterscannen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Artikel Katalog -->
    <v-dialog v-model="dialogs.artikelKatalog" max-width="1200" scrollable>
      <v-card class="dialog-card">
        <v-card-title>
          Artikel-Katalog
          <v-spacer />
          <v-btn icon @click="dialogs.artikelKatalog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-data-table
            :headers="[
              { title: 'CMMF', key: 'cmmf_nummer' },
              { title: 'EAN', key: 'gtin_ean' },
              { title: 'Artikel', key: 'kurztext' },
              { title: 'Werk', key: 'werk' },
              { title: 'Lagerort', key: 'lagerort' },
              { title: 'Sollbestand', key: 'sollbestand' }
            ]"
            :items="artikelListe"
            density="compact"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog: Palettenschein drucken -->
    <v-dialog v-model="dialogs.palettenschein" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-printer</v-icon>
          Palettenschein drucken
        </v-card-title>
        <v-card-text>
          <p class="mb-4">Gepackt von und Kontrolle (optional) auswählen. „Gescannt von" wird automatisch gesetzt.</p>
          <v-autocomplete
            v-model="palettenscheinData.ansager"
            :items="userOptions"
            :loading="userOptionsLoading"
            label="Gepackt von"
            class="mb-2"
            hide-details
            clearable
            prepend-inner-icon="mdi-account"
            no-data-text="Keine Mitarbeiter gefunden"
          />
          <v-text-field
            v-model="palettenscheinData.schreiber"
            label="Gescannt von"
            class="mb-2"
            hide-details
            prepend-inner-icon="mdi-account-check"
            hint="Automatisch ermittelt aus den Scans dieser Palette – bei Bedarf editierbar"
            persistent-hint
          />
          <v-autocomplete
            v-model="palettenscheinData.kontrolle"
            :items="userOptions"
            :loading="userOptionsLoading"
            label="Kontrolle (optional)"
            hide-details
            clearable
            prepend-inner-icon="mdi-account-search"
            no-data-text="Keine Mitarbeiter gefunden"
            class="mt-4"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogs.palettenschein = false">Abbrechen</v-btn>
          <v-btn color="primary" @click="executePrintPalettenschein">
            <v-icon start>mdi-printer</v-icon>
            Drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import axios from 'axios';
import { API_BASE_URL } from '@/config';

const authStore = useAuthStore();
const toast = useToast();
const inventurApiUrl = (path = 'index.php') => `inventur/${path}`;
const inventurClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true
});

// ========================================
// STATE
// ========================================

const scannerAktiv = ref(false);
const selectedPalette = ref(null);
const paletten = ref([]);
const positionen = ref([]);
const palettenSuche = ref('');
const statusFilter = ref('alle');
const scanInput = ref('');
const scanMenge = ref(1);
const artikelListe = ref([]);

const scannerInput = ref(null);
const statistik = ref({
  offene_paletten: 0,
  abgeschlossene_paletten: 0,
  gesamt_positionen: 0,
  gesamt_istbestand: 0
});

const loading = ref({
  paletten: false,
  positionen: false,
  scanning: false,
  artikel: false
});

const dialogs = ref({
  artikelKatalog: false,
  palettenschein: false,
  manualSearch: false,
  scanMultiHit: false,
  eanNotFound: false
});

// EAN, die beim letzten Scan nicht im Katalog gefunden wurde
// (wird im Dialog "Artikel nicht gefunden" angezeigt).
const notFoundEan = ref('');

const manualSearch = ref({
  selected: null,
  menge: 1
});

const scanMultiHit = ref({
  ean: '',
  menge: 1,
  artikel: []
});

const palettenscheinData = ref({
  ansager: '',
  schreiber: '',
  kontrolle: '',
  palette_id: null
});

// Mitarbeiter-Liste fuer Dropdowns "Gepackt von" / "Kontrolle"
const userOptions = ref([]);
const userOptionsLoading = ref(false);

const fetchUserList = async () => {
  userOptionsLoading.value = true;
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'get_user_list' }
    });
    if (response.data?.status === 'success' && Array.isArray(response.data.users)) {
      userOptions.value = response.data.users.map(u => u.fullname);
    }
  } catch (error) {
    console.error('Fehler beim Laden der Mitarbeiterliste:', error);
  } finally {
    userOptionsLoading.value = false;
  }
};

const scanFeedback = ref({
  show: false,
  type: 'info',
  message: ''
});

// ========================================
// COMPUTED
// ========================================

const gesamtStueckzahl = computed(() => {
  return positionen.value.reduce((sum, pos) => sum + (parseInt(pos.istbestand) || 0), 0);
});

const filteredPaletten = computed(() => {
  let filtered = paletten.value;

  if (statusFilter.value !== 'alle') {
    filtered = filtered.filter(p => p.status === statusFilter.value);
  }

  if (!palettenSuche.value) return filtered;

  const suche = palettenSuche.value.toLowerCase();
  return filtered.filter(p =>
    p.paletten_nr?.toLowerCase().includes(suche) ||
    p.erstellt_von_name?.toLowerCase().includes(suche)
  );
});

const palettenTableHeaders = computed(() => [
  { title: 'Paletten-Nr.', key: 'paletten_nr', sortable: true },
  { title: 'Erstellt von', key: 'erstellt_von_name', sortable: true },
  { title: 'Erstellt am', key: 'erfassung_datum', sortable: true },
  { title: 'Artikel', key: 'anzahl_positionen', sortable: true },
  { title: 'Stückzahl', key: 'gesamtistbestand', sortable: true },
  { title: 'Status', key: 'status', sortable: true },
  { title: 'Aktionen', key: 'actions', sortable: false }
]);

const positionenTableHeaders = computed(() => [
  { title: 'Pos.', key: 'pos_nr', sortable: false, width: 60 },
  { title: 'CMMF', key: 'cmmf_nummer' },
  { title: 'EAN', key: 'gtin_ean' },
  { title: 'Artikel', key: 'kurztext' },
  { title: 'Werk', key: 'werk' },
  { title: 'Lagerort', key: 'lagerort' },
  { title: 'IST', key: 'istbestand' },
  { title: 'Aktionen', key: 'actions' }
]);

// Positionen mit fortlaufender Nummer (laut Erfassungsreihenfolge)
const positionenMitNr = computed(() =>
  positionen.value.map((p, idx) => ({ ...p, pos_nr: idx + 1 }))
);

// ========================================
// METHODS - Palette Verwaltung
// ========================================

const openCreatePaletteDialog = async () => {
  try {
    const response = await inventurClient.post(inventurApiUrl(), {
      action: 'create_palette'
    });

    if (response.data.status === 'success') {
      selectedPalette.value = {
        id: response.data.palette_id,
        paletten_nr: response.data.paletten_nr,
        status: 'erfassung',
        erstellt_von_name: authStore.user.vorname + ' ' + authStore.user.nachname,
        erfassung_datum: new Date().toISOString()
      };
      positionen.value = [];
      scannerAktiv.value = true;
      await nextTick();
      scannerInput.value?.focus();
      toast.success('Neue Inventur-Palette erstellt: ' + response.data.paletten_nr);
    }
  } catch (error) {
    console.error('Fehler beim Erstellen der Palette:', error);
    toast.error('Fehler beim Erstellen der Palette');
  }
};

const fetchPaletten = async () => {
  loading.value.paletten = true;
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'get_paletten' }
    });

    if (response.data.status === 'success') {
      paletten.value = response.data.paletten || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Paletten:', error);
    toast.error('Netzwerkfehler');
  } finally {
    loading.value.paletten = false;
  }
};

const loadPalette = async (paletteId) => {
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'get_palette', palette_id: paletteId }
    });

    if (response.data.status === 'success') {
      selectedPalette.value = response.data.palette;
      positionen.value = response.data.positionen || [];
      scannerAktiv.value = true;
      await nextTick();
      scannerInput.value?.focus();
    }
  } catch (error) {
    console.error('Fehler beim Laden der Palette:', error);
    toast.error('Fehler beim Laden der Palette');
  }
};

const deaktiviereScanner = async () => {
  scannerAktiv.value = false;
  selectedPalette.value = null;
  positionen.value = [];
  scanInput.value = '';
  scanMenge.value = 1;
  await fetchPaletten();
  await fetchStatistik();
};

// ========================================
// METHODS - Scanner
// ========================================

const handleScan = async () => {
  if (!scanInput.value.trim()) {
    showScanFeedback('warning', 'Bitte einen Code eingeben');
    return;
  }

  loading.value.scanning = true;
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'search_ean', ean: scanInput.value.trim() }
    });

    if (response.data.status === 'success' && response.data.artikel.length > 0) {
      const trefferListe = response.data.artikel;
      const lookupInfo = response.data.ean_lookup || null;

      // Wenn mehrere Artikel auf denselben EAN/CMMF passen, User auswählen lassen
      if (trefferListe.length > 1) {
        scanMultiHit.value = {
          ean: scanInput.value.trim(),
          menge: scanMenge.value || 1,
          artikel: trefferListe
        };
        dialogs.value.scanMultiHit = true;
        return;
      }

      const artikel = trefferListe[0];

      await inventurClient.post(inventurApiUrl(), {
        action: 'scan_article',
        palette_id: selectedPalette.value.id,
        katalog_id: artikel.id,
        istbestand: scanMenge.value
      });

      await loadPalette(selectedPalette.value.id);

      // Wenn der Treffer aus der EAN-Lookup-Tabelle kam, dem User klar zeigen,
      // dass die EAN am bestehenden Katalog-Artikel ergänzt wurde.
      if (lookupInfo) {
        showScanFeedback(
          'info',
          `EAN aus Lookup übernommen → CMMF ${lookupInfo.cmmf_nummer}: ${artikel.kurztext || ''}`
        );
      } else {
        showScanFeedback('success', `Artikel gescannt: ${artikel.kurztext}`);
      }
      scanInput.value = '';
      scanMenge.value = 1;
      scannerInput.value?.focus();
    } else {
      // Prominenter Hinweis: gescannter Code ist im Katalog nicht vorhanden.
      // Wird leicht uebersehen, wenn er nur als kleine Alert-Zeile erscheint.
      notFoundEan.value = scanInput.value.trim();
      scanInput.value = '';
      scanMenge.value = 1;
      dialogs.value.eanNotFound = true;
    }
  } catch (error) {
    console.error('Fehler beim Scannen:', error);
    showScanFeedback('error', 'Fehler beim Scannen');
  } finally {
    loading.value.scanning = false;
  }
};

const confirmScanMultiHit = async (artikel) => {
  if (!artikel || !selectedPalette.value) return;
  loading.value.scanning = true;
  try {
    await inventurClient.post(inventurApiUrl(), {
      action: 'scan_article',
      palette_id: selectedPalette.value.id,
      katalog_id: artikel.id,
      istbestand: scanMultiHit.value.menge
    });
    await loadPalette(selectedPalette.value.id);
    showScanFeedback('success', `Artikel gescannt: ${artikel.kurztext} (CMMF ${artikel.cmmf_nummer})`);
    dialogs.value.scanMultiHit = false;
    scanMultiHit.value = { ean: '', menge: 1, artikel: [] };
    scanInput.value = '';
    scanMenge.value = 1;
    scannerInput.value?.focus();
  } catch (error) {
    console.error('Fehler bei Auswahl Mehrfach-Treffer:', error);
    showScanFeedback('error', 'Fehler beim Erfassen des Artikels');
  } finally {
    loading.value.scanning = false;
  }
};

const cancelScanMultiHit = () => {
  dialogs.value.scanMultiHit = false;
  scanMultiHit.value = { ean: '', menge: 1, artikel: [] };
  scannerInput.value?.focus();
};

const closeEanNotFound = () => {
  dialogs.value.eanNotFound = false;
  notFoundEan.value = '';
  // Fokus wieder ins Scan-Feld, damit sofort der nächste Code gescannt werden kann.
  nextTick(() => {
    scannerInput.value?.focus();
  });
};

const manualSearchItemTitle = (item) => {
  if (!item) return '';
  const parts = [];
  if (item.kurztext) parts.push(item.kurztext);
  if (item.cmmf_nummer) parts.push(`CMMF ${item.cmmf_nummer}`);
  if (item.gtin_ean) parts.push(`EAN ${item.gtin_ean}`);
  return parts.join(' · ');
};

const manualSearchFilter = (_value, query, item) => {
  const q = String(query || '').toLowerCase().trim();
  if (!q) return true;
  const raw = item?.raw || {};
  const haystack = [
    raw.kurztext,
    raw.cmmf_nummer,
    raw.gtin_ean,
    raw.werk,
    raw.lagerort
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
};

const openManualSearch = () => {
  if (selectedPalette.value && selectedPalette.value.status !== 'erfassung') {
    showScanFeedback('warning', 'Palette ist nicht in Erfassung');
    return;
  }
  if (!artikelListe.value || artikelListe.value.length === 0) {
    fetchArtikelListe();
  }
  manualSearch.value = { selected: null, menge: scanMenge.value || 1 };
  dialogs.value.manualSearch = true;
};

const addManualArticle = async () => {
  const artikel = manualSearch.value.selected;
  const menge = Number(manualSearch.value.menge) || 0;
  if (!artikel || menge < 1) return;

  loading.value.scanning = true;
  try {
    // Artikel auf Palette buchen (EAN-Pflege erfolgt zentral im Office)
    await inventurClient.post(inventurApiUrl(), {
      action: 'scan_article',
      palette_id: selectedPalette.value.id,
      katalog_id: artikel.id,
      istbestand: menge
    });
    await loadPalette(selectedPalette.value.id);
    showScanFeedback('success', `Artikel hinzugefügt: ${artikel.kurztext || artikel.cmmf_nummer}`);
    dialogs.value.manualSearch = false;
    scannerInput.value?.focus();
  } catch (error) {
    console.error('Fehler beim manuellen Hinzufügen:', error);
    showScanFeedback('error', 'Fehler beim Hinzufügen');
  } finally {
    loading.value.scanning = false;
  }
};

const removePosition = async (positionId) => {
  try {
    await inventurClient.post(inventurApiUrl(), {
      action: 'remove_position',
      position_id: positionId
    });

    positionen.value = positionen.value.filter(p => p.id !== positionId);
    showScanFeedback('info', 'Position entfernt');
  } catch (error) {
    console.error('Fehler beim Entfernen:', error);
    showScanFeedback('error', 'Fehler beim Entfernen');
  }
};

const deletingPaletteId = ref(null);

const canDeletePalette = (palette) => {
  if (!palette) return false;
  const u = authStore.user || {};
  const isOffice = !!(u.office || u.admin || u.super_admin || u.developer);
  const positionen = Number(palette.anzahl_positionen ?? 0);
  if (positionen > 0) return false;
  if (isOffice) return true;
  if (palette.status !== 'erfassung') return false;
  return Number(palette.erstellt_von) === Number(u.id);
};

const deleteEmptyPalette = async (palette) => {
  if (!confirm(`Leere Palette ${palette.paletten_nr} wirklich löschen?`)) return;
  deletingPaletteId.value = palette.id;
  try {
    const response = await inventurClient.post(inventurApiUrl(), {
      action: 'delete_palette',
      palette_id: palette.id
    });
    if (response.data?.status === 'success') {
      toast.success(response.data.message || 'Palette gelöscht');
      await fetchPaletten();
    } else {
      toast.error(response.data?.message || 'Fehler beim Löschen');
    }
  } catch (error) {
    console.error(error);
    toast.error(error.response?.data?.message || 'Fehler beim Löschen der Palette');
  } finally {
    deletingPaletteId.value = null;
  }
};

const closeSelectedPalette = async () => {
  if (!selectedPalette.value) {
    return;
  }

  const confirmed = window.confirm(`Palette ${selectedPalette.value.paletten_nr} wirklich abschließen?`);
  if (!confirmed) {
    return;
  }

  try {
    await inventurClient.post(inventurApiUrl(), {
      action: 'close_palette',
      palette_id: selectedPalette.value.id
    });

    showScanFeedback('success', 'Palette abgeschlossen');
    toast.success('Palette abgeschlossen');

    // Palettenschein-Dialog öffnen
    // "Gescannt von" = tatsächliche Scanner laut Positionen (falls vorhanden),
    // sonst Ersteller der Palette, sonst aktuell angemeldeter User.
    const erstellerSelf = ((authStore.user?.vorname || '') + ' ' + (authStore.user?.nachname || '')).trim();
    // Frische Scanner-Liste aus den Positionen nachladen (selectedPalette wurde
    // u. U. ohne scanner_names geladen).
    let scanners = '';
    try {
      const detailResp = await inventurClient.get(inventurApiUrl(), {
        params: { action: 'get_palette', palette_id: selectedPalette.value.id }
      });
      if (detailResp.data?.status === 'success' && detailResp.data.palette) {
        scanners = String(detailResp.data.palette.scanner_names || '').trim();
      }
    } catch (e) {
      // Fallback auf vorhandenes Feld – nicht kritisch
      scanners = String(selectedPalette.value.scanner_names || '').trim();
    }
    palettenscheinData.value = {
      ansager: '',
      schreiber: (scanners || selectedPalette.value.erstellt_von_name || erstellerSelf || '').trim(),
      kontrolle: '',
      palette_id: selectedPalette.value.id
    };
    if (userOptions.value.length === 0 && !userOptionsLoading.value) {
      fetchUserList();
    }
    dialogs.value.palettenschein = true;
  } catch (error) {
    console.error('Fehler beim Abschließen der Palette:', error);
    showScanFeedback('error', 'Fehler beim Abschließen der Palette');
    toast.error('Palette konnte nicht abgeschlossen werden');
  }
};

const openPalettenscheinDialog = (palette) => {
  // "Gescannt von" = tatsächliche Scanner laut Positionen (falls vorhanden),
  // sonst Ersteller der Palette.
  const scanners = (palette && palette.scanner_names)
    ? String(palette.scanner_names).trim()
    : '';
  const ersteller = (palette && palette.erstellt_von_name)
    ? String(palette.erstellt_von_name).trim()
    : '';
  palettenscheinData.value = {
    ansager: '',
    schreiber: scanners || ersteller,
    kontrolle: '',
    palette_id: palette.id
  };
  if (userOptions.value.length === 0 && !userOptionsLoading.value) {
    fetchUserList();
  }
  dialogs.value.palettenschein = true;
};

const executePrintPalettenschein = async () => {
  const paletteId = palettenscheinData.value.palette_id;
  const ansager = palettenscheinData.value.ansager || '';
  const schreiber = palettenscheinData.value.schreiber || '';
  const kontrolle = palettenscheinData.value.kontrolle || '';
  dialogs.value.palettenschein = false;
  await printPalettenschein(paletteId, ansager, schreiber, kontrolle);
  // Nach Druck zur Übersicht
  await deaktiviereScanner();
};

const printPalettenschein = async (paletteId, ansager = '', schreiber = '', kontrolle = '') => {
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'get_palette', palette_id: paletteId }
    });
    if (response.data?.status !== 'success') {
      toast.error('Palette konnte nicht geladen werden');
      return;
    }
    const palette = response.data.palette;
    const positionen = response.data.positionen || [];

    // Druck-Log eintragen (best effort) und Drucknummer auswerten
    let drucknummer = 1;
    let druckdatum = new Date().toLocaleString('de-DE');
    let auditAnchor = null;
    try {
      const druckResp = await inventurClient.post(inventurApiUrl(), {
        action: 'log_palettenschein_druck',
        palette_id: paletteId,
        ansager_name: ansager,
        schreiber_name: schreiber
      });
      if (druckResp?.data?.status === 'success') {
        drucknummer = Number(druckResp.data.drucknummer || 1);
        if (druckResp.data.druckdatum) {
          druckdatum = new Date(String(druckResp.data.druckdatum).replace(' ', 'T'))
            .toLocaleString('de-DE');
        }
        if (druckResp.data.audit_anchor) {
          auditAnchor = druckResp.data.audit_anchor;
        }
      }
    } catch (e) { /* nicht kritisch */ }

    const { html, footerLeft } = buildPalettenscheinHtml(palette, positionen, ansager, schreiber, drucknummer, druckdatum, auditAnchor, kontrolle);

    // Pagination im Hauptfenster (versteckter iframe) ausfuehren, damit
    // das Druck-Popup ohne Inline-Script auskommt und nicht von strengen
    // Content-Security-Policies blockiert wird.
    const measure = document.createElement('iframe');
    measure.setAttribute('aria-hidden', 'true');
    measure.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:600mm;border:0;visibility:hidden;';
    document.body.appendChild(measure);
    const idoc = measure.contentDocument || measure.contentWindow.document;
    idoc.open();
    idoc.write(html);
    idoc.close();
    await new Promise((resolve) => {
      if (idoc.readyState === 'complete') resolve();
      else measure.addEventListener('load', () => resolve(), { once: true });
    });
    try {
      paginatePalettenscheinDoc(idoc, footerLeft);
    } catch (e) {
      console.error('Pagination fehlgeschlagen', e);
    }
    const finalHtml = '<!DOCTYPE html>\n' + idoc.documentElement.outerHTML;
    measure.remove();

    const win = window.open('', '_blank', 'width=900,height=900');
    if (!win) {
      toast.error('Popup blockiert – Drucken nicht möglich');
      return;
    }
    win.document.open();
    win.document.write(finalHtml);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch (e) { console.warn(e); } }, 350);
  } catch (error) {
    console.error(error);
    toast.error('Fehler beim Drucken des Palettenscheins');
  }
};

const buildPalettenscheinHtml = (palette, positionen, ansager, schreiber, drucknummer = 1, druckDatum = null, auditAnchor = null, kontrolle = '') => {
  const datum = palette.erfassung_datum
    ? formatDeDateTime(palette.erfassung_datum)
    : formatDeDateTime(new Date());
  const druckDatumStr = druckDatum || formatDeDateTime(new Date());
  const istNachdruck = Number(drucknummer) > 1;
  let gesamtIst = 0;
  const rows = positionen.map(p => {
    const ist = Number(p.istbestand || 0);
    gesamtIst += ist;
    return `<tr><td>${escapeHtml(p.cmmf_nummer || '')}</td><td>${escapeHtml(p.kurztext || '')}</td>` +
      `<td>${escapeHtml(p.werk || '')}</td><td>${escapeHtml(p.lagerort || '')}</td>` +
      `<td style="text-align:center;">${ist}</td></tr>`;
  }).join('');
  const totalsRow = `<tr class="totals"><td colspan="4"><strong>GESAMT</strong></td><td style="text-align:center;"><strong>${gesamtIst}</strong></td></tr>`;
  const anchorHash = auditAnchor && auditAnchor.hash ? String(auditAnchor.hash) : '';
  const anchorLogId = auditAnchor && auditAnchor.log_id != null ? String(auditAnchor.log_id) : '';
  const anchorAt = auditAnchor && auditAnchor.anchor_at
    ? formatDeDateTime(auditAnchor.anchor_at)
    : '';
  const anchorBlock = anchorHash
    ? `<div class="anchor" data-block="anchor">
         <div class="anchor-title">Hash-Anker (GoBD &middot; Audit-Log SHA-256)</div>
         <div class="anchor-meta">Audit-Log-Eintrag #${escapeHtml(anchorLogId)} vom ${escapeHtml(anchorAt)}</div>
         <div class="anchor-hash">${escapeHtml(anchorHash)}</div>
         <div class="anchor-info">Dieser SHA-256-Hash ankert die gesamte Inventur-Audit-Kette zum Druckzeitpunkt. Stimmt er bei einer sp&auml;teren Pr&uuml;fung mit dem record_hash in der Datenbank bzw. CSV-Export &uuml;berein, ist bewiesen, dass seit der Unterschrift kein Datensatz in der Kette ver&auml;ndert wurde.</div>
         <div class="anchor-sigs">
           <div class="sig"><div class="sig-line"></div><div class="sig-cap">Hash-Anker best&auml;tigt (Unterschrift / Datum)</div></div>
           <div class="sig"><div class="sig-line"></div><div class="sig-cap">Inventurleitung (Unterschrift / Datum)</div></div>
         </div>
       </div>`
    : '<div class="anchor anchor-empty" data-block="anchor">Kein Audit-Log-Anker verf&uuml;gbar.</div>';

  const titelOriginal = istNachdruck
    ? `PALETTENSCHEIN (NACHDRUCK Nr. ${drucknummer} vom ${druckDatumStr})`
    : 'PALETTENSCHEIN (ORIGINAL)';
  const titelKopie = istNachdruck
    ? `PALETTENSCHEIN (NACHDRUCK Nr. ${drucknummer} – KOPIE vom ${druckDatumStr})`
    : 'PALETTENSCHEIN (KOPIE)';
  const pageFooterLeft = `Palettenschein ${escapeHtml(palette.paletten_nr || '')} · Druck: ${escapeHtml(druckDatumStr)}${istNachdruck ? ' · Druck-Nr. ' + drucknummer : ''}`;

  const scheinFlow = (titel) => `
    <div class="schein-flow">
      <div class="header" data-block="header">
        <div class="header-logo">${dhsLogoSvg}</div>
        <div class="header-title">${titel}</div>
        <div class="header-spacer"></div>
      </div>
      <table class="info" data-block="info">
        <tr><td class="lbl">Paletten-Nr.:</td><td><strong>${escapeHtml(palette.paletten_nr || '')}</strong></td>
            <td class="lbl">Datum:</td><td>${escapeHtml(datum)}</td></tr>
      </table>
      <table class="positions" data-block="positions">
        <thead><tr><th>CMMF</th><th>Artikel</th><th>Werk</th><th>Lagerort</th><th>IST</th></tr></thead>
        <tbody>
          ${rows || '<tr><td colspan="5" style="text-align:center;color:#888;">Keine Positionen</td></tr>'}
          ${totalsRow}
        </tbody>
      </table>
      <div class="signatures" data-block="sig1">
        <div class="sig"><div class="sig-lbl">Gepackt von</div><div class="sig-line">${escapeHtml(ansager)}</div><div class="sig-cap">Unterschrift / Datum</div></div>
        <div class="sig"><div class="sig-lbl">Gescannt von</div><div class="sig-line">${escapeHtml(schreiber)}</div><div class="sig-cap">Unterschrift / Datum</div></div>
      </div>
      <div class="signatures" data-block="sig2">
        <div class="sig"><div class="sig-lbl">Kontrolle</div><div class="sig-line">${escapeHtml(kontrolle)}</div><div class="sig-cap">Unterschrift / Datum</div></div>
      </div>
      ${anchorBlock}
    </div>`;

  const html = `<html><head><meta charset="UTF-8"><title>Palettenschein ${escapeHtml(palette.paletten_nr || '')}${istNachdruck ? ` – Nachdruck Nr. ${drucknummer}` : ''}</title>
    <style>
      @page { size: A4; margin: 12mm; }
      body { font-family: Arial, sans-serif; font-size: 11pt; margin: 0; padding: 0; color: #000; }

      #content-flow { width: 186mm; box-sizing: border-box; margin: 0 auto; }
      .schein-flow { box-sizing: border-box; }

      .header {
        display: flex;
        align-items: center;
        gap: 6mm;
        border-bottom: 2px solid #000;
        padding-bottom: 4mm;
        margin-bottom: 6mm;
        ${istNachdruck ? 'color: #b71c1c;' : ''}
      }
      .header-logo { flex: 0 0 32mm; }
      .header-logo svg { display: block; width: 100%; height: auto; }
      .header-title { flex: 1; text-align: center; font-size: 18pt; font-weight: bold; }
      .header-spacer { flex: 0 0 32mm; }
      table.info { width: 100%; border-collapse: collapse; margin-bottom: 6mm; }
      table.info td { padding: 3mm 4mm; border: 1px solid #ccc; }
      table.info td.lbl { background: #f0f0f0; font-weight: bold; width: 18%; }
      table.positions { width: 100%; border-collapse: collapse; margin-bottom: 8mm; }
      table.positions th, table.positions td { border: 1px solid #000; padding: 2.5mm 3mm; font-size: 10pt; }
      table.positions th { background: #e8e8e8; }
      table.positions thead { display: table-header-group; }
      tr { page-break-inside: avoid; }
      tr.totals td { background: #f0f0f0; }
      .signatures { display: flex; justify-content: space-between; margin-top: 8mm; gap: 10mm; page-break-inside: avoid; }
      .sig { width: 48%; }
      .sig-lbl { font-weight: bold; margin-bottom: 14mm; }
      .sig-line { border-bottom: 1px solid #000; min-height: 6mm; padding: 0 2mm; }
      .sig-cap { font-size: 9pt; color: #555; text-align: center; margin-top: 1mm; }

      .anchor { margin-top: 8mm; border: 1px solid #000; padding: 4mm; font-size: 9pt; page-break-inside: avoid; }
      .anchor-title { font-weight: bold; font-size: 10pt; margin-bottom: 2mm; }
      .anchor-meta { font-size: 9pt; color: #333; }
      .anchor-hash { margin-top: 2mm; font-family: 'Courier New', monospace; font-size: 8pt; word-break: break-all; background: #f7f7f7; padding: 2mm; border: 1px dashed #999; }
      .anchor-info { margin-top: 3mm; font-size: 8pt; color: #444; }
      .anchor-sigs { display: flex; justify-content: space-between; gap: 8mm; margin-top: 6mm; }
      .anchor-sigs .sig { width: 48%; }
      .anchor-sigs .sig-line { border-bottom: 1px solid #000; min-height: 10mm; }
      .anchor-sigs .sig-cap { font-size: 8pt; color: #555; text-align: center; margin-top: 1mm; }
      .anchor-empty { color: #888; font-style: italic; }

      .print-page {
        position: relative;
        box-sizing: border-box;
        width: 186mm;
        height: 273mm;
        padding-bottom: 14mm;
        overflow: hidden;
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      .print-page:last-child { page-break-after: auto; break-after: auto; }
      .page-footer {
        position: absolute;
        left: 0; right: 0; bottom: 0;
        display: flex; justify-content: space-between; align-items: center;
        font-size: 8pt; color: #555;
        border-top: 1px solid #999;
        padding: 1.2mm 1mm 0 1mm;
      }
      .page-footer .pf-page { font-weight: bold; color: #000; }
    </style></head><body>
    <div id="content-flow">
      ${scheinFlow(titelOriginal)}
      ${scheinFlow(titelKopie)}
    </div>
    </body></html>`;
  return { html, footerLeft: pageFooterLeft };
};

// Pagination im Hauptfenster (nicht im Popup), damit das Druck-HTML
// frei von Inline-Scripts bleibt und nicht von Content-Security-Policy
// blockiert werden kann.
const paginatePalettenscheinDoc = (doc, footerLeft) => {
  const PX_PER_MM = 96 / 25.4;
  const FOOTER_RESERVE_PX = 16 * PX_PER_MM;
  const SAFETY_PX = 12 * PX_PER_MM;
  const PAGE_PX = Math.floor(273 * PX_PER_MM) - FOOTER_RESERVE_PX - SAFETY_PX;

  const scheine = Array.prototype.slice.call(doc.querySelectorAll('.schein-flow'));
  if (scheine.length === 0) return;

  const allPages = [];
  const scheinRanges = [];
  let currentPage = null;
  let currentH = 0;

  const startNewPage = () => {
    currentPage = doc.createElement('div');
    currentPage.className = 'print-page';
    allPages.push(currentPage);
    currentH = 0;
  };

  scheine.forEach((schein) => {
    const rangeStart = allPages.length;
    startNewPage();

    const blocks = Array.prototype.slice.call(schein.children);
    blocks.forEach((blk) => {
      const kind = blk.getAttribute('data-block');
      if (kind === 'positions') {
        const thead = blk.querySelector('thead');
        const theadH = thead ? thead.offsetHeight : 0;
        const theadHtml = thead ? thead.outerHTML : '';
        const trs = Array.prototype.slice.call(blk.querySelectorAll('tbody > tr'));
        const trHs = trs.map((r) => r.offsetHeight);

        const makeTbl = () => {
          const tbl = doc.createElement('table');
          tbl.className = 'positions';
          tbl.innerHTML = theadHtml + '<tbody></tbody>';
          return tbl;
        };
        let tbl = makeTbl();
        let tbody = tbl.querySelector('tbody');
        currentPage.appendChild(tbl);
        currentH += theadH + 8;

        for (let i = 0; i < trs.length; i++) {
          if (currentH + trHs[i] > PAGE_PX && tbody.children.length > 0) {
            startNewPage();
            tbl = makeTbl();
            tbody = tbl.querySelector('tbody');
            currentPage.appendChild(tbl);
            currentH = theadH + 8;
          }
          tbody.appendChild(trs[i].cloneNode(true));
          currentH += trHs[i];
        }
      } else {
        const h = blk.offsetHeight + 8;
        if (currentH + h > PAGE_PX && currentPage.children.length > 0) {
          startNewPage();
        }
        currentPage.appendChild(blk.cloneNode(true));
        currentH += h;
      }
    });
    scheinRanges.push({ start: rangeStart, end: allPages.length });
  });

  const container = doc.createElement('div');
  scheinRanges.forEach((rng) => {
    const subTotal = rng.end - rng.start;
    for (let idx = 0; idx < subTotal; idx++) {
      const p = rng.start + idx;
      const footer = doc.createElement('div');
      footer.className = 'page-footer';
      const left = doc.createElement('span');
      left.textContent = footerLeft;
      const right = doc.createElement('span');
      right.className = 'pf-page';
      right.textContent = `Seite ${idx + 1} von ${subTotal}`;
      footer.appendChild(left);
      footer.appendChild(right);
      allPages[p].appendChild(footer);
      container.appendChild(allPages[p]);
    }
  });
  const oldFlow = doc.getElementById('content-flow');
  if (oldFlow) oldFlow.remove();
  doc.body.appendChild(container);
};

const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

// Deutsches Datum/Uhrzeit immer mit fuehrenden Nullen: DD.MM.YYYY HH:mm
const formatDeDateTime = (dateTime) => {
  if (!dateTime) return '';
  const d = (dateTime instanceof Date) ? dateTime : new Date(String(dateTime).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(dateTime);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// DHS Firmenlogo (Inline-SVG, identisch zu VersandView.vue)
const dhsLogoSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 332.18 94.77"><path fill="#2f5e30" d="m.04,8.52h26.13c18.64,0,28.04,11.38,28.04,24.96,0,15.93-11.67,26.64-28.19,26.64H.04V8.52Zm16.74,40h7.27c8.29,0,12.48-4.99,12.48-14.68,0-7.93-3.3-13.73-12.48-13.8h-7.27v28.48Z"/><path fill="#2f5e30" d="m61.25,8.52h16.74v18.64h18.64V8.52h16.81v51.6h-16.81v-21.07h-18.64v21.07h-16.74V8.52Z"/><path fill="#2f5e30" d="m137.51,44.56c1.1,3.45,3.74,6.02,10.5,6.02,5.95,0,7.78-1.84,7.78-4.41,0-2.72-1.61-4.33-11.3-6.02-19.09-3.3-21.8-9.84-21.8-16.66,0-10.2,9.61-16.15,23.71-16.15,17.18,0,24,7.93,24.66,15.56h-16.29c-.59-2.28-1.98-4.92-8.44-4.92-4.55,0-6.68,1.47-6.68,3.89s2.13,3.52,10.2,4.99c19.45,3.45,23.27,9.62,23.27,17.4,0,9.84-8.29,16.95-25.84,16.95s-25.03-7.34-26.57-16.66h16.81Z"/><g><path fill="#f4980c" d="m0,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76H1.31v5.91H0v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76H1.31v5.52h5.14Z"/><path fill="#f4980c" d="m13.92,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m26.9,80.73h5.72c2.33,0,3.9,1.64,3.9,4.11s-1.57,4.12-3.9,4.12h-4.41v5.52h-1.31v-13.74Zm5.72,7.07c1.55,0,2.6-1.19,2.6-2.96s-1.05-2.95-2.6-2.95h-4.41v5.91h4.41Z"/><path fill="#f4980c" d="m42.73,80.73h1.31l5.56,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.55-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/><path fill="#f4980c" d="m52.37,80.73h1.31v13.74h-1.31v-13.74Z"/><path fill="#f4980c" d="m57.58,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m76.72,87.59c0-4.29,2.54-7.16,6.34-7.16s6.35,2.87,6.35,7.16-2.55,7.18-6.35,7.18-6.34-2.88-6.34-7.18Zm11.38,0c0-3.59-2.03-6-5.04-6s-5.03,2.41-5.03,6,2.02,6.02,5.03,6.02,5.04-2.42,5.04-6.02Z"/><path fill="#f4980c" d="m92.82,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m112.47,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m126.39,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m139.37,80.73h1.27l7.95,11.59v-11.59h1.27v13.74h-1.27l-7.95-11.59v11.59h-1.27v-13.74Z"/><path fill="#f4980c" d="m153.76,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m165.61,80.73h1.28l3.26,11.64,3.27-11.64h1.28l3.26,11.64,3.27-11.64h1.28l-3.89,13.74h-1.31l-3.24-11.43-3.26,11.43h-1.31l-3.88-13.74Z"/><path fill="#f4980c" d="m191.01,80.73h1.64l4.96,12.01,4.97-12.01h1.64v13.74h-1.21v-11.59l-4.8,11.59h-1.19l-4.8-11.59v11.59h-1.21v-13.74Z"/><path fill="#f4980c" d="m208.13,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m225.55,80.73h1.31l5.55,13.74h-1.32l-1.62-4.01h-6.53l-1.62,4.01h-1.32l5.56-13.74Zm3.48,8.57l-2.83-6.95-2.83,6.95h5.66Z"/><path fill="#f4980c" d="m235.19,80.73h1.27l7.94,11.59v-11.59h1.27v13.74h-1.27l-7.94-11.59v11.59h-1.27v-13.74Z"/><path fill="#f4980c" d="m248.86,90.33h1.31c0,1.96,1.61,3.29,4,3.29s4.01-1.09,4.01-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/><path fill="#f4980c" d="m268.4,80.73h6.44c2.23,0,3.73,1.57,3.73,3.92,0,1.72-.83,3.02-2.15,3.55,1.06.49,1.67,1.57,1.85,3.13l.35,3.15h-1.28l-.35-3.15c-.18-1.66-.97-2.76-2.42-2.76h-4.86v5.91h-1.31v-13.74Zm6.45,6.67c1.44,0,2.41-1.11,2.41-2.75s-.97-2.76-2.42-2.76h-5.13v5.52h5.14Z"/><path fill="#f4980c" d="m282.32,80.73h9.32v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/><path fill="#f4980c" d="m295.31,90.16v-9.43h1.31v9.43c0,2.06,1.53,3.45,3.82,3.45s3.83-1.39,3.83-3.46v-9.42h1.31v9.43c0,2.75-2.05,4.61-5.13,4.61s-5.12-1.86-5.12-4.61Z"/><path fill="#f4980c" d="m308.76,90.33h1.31c0,1.96,1.62,3.29,4.01,3.29s4-1.09,4-2.71c0-2-2.01-2.47-4.09-2.95-2.33-.55-4.83-1.19-4.83-3.86,0-2.19,1.97-3.66,4.91-3.66s4.92,1.61,4.92,4h-1.31c0-1.71-1.45-2.85-3.61-2.85s-3.6,1-3.6,2.5c0,1.78,1.87,2.22,3.88,2.68,2.39.56,5.03,1.22,5.03,4.14,0,2.32-2.14,3.86-5.31,3.86s-5.31-1.78-5.31-4.44Z"/><path fill="#f4980c" d="m322.57,80.73h9.33v1.16h-8.02v5.13h7.44v1.16h-7.44v5.13h8.3v1.16h-9.61v-13.74Z"/></g><g><path fill="#f4980c" fill-rule="evenodd" d="m269.62,34.92c4.82-.26,8.53-4.39,8.27-9.21-.26-4.82-4.39-8.53-9.21-8.27-4.82.26-8.53,4.39-8.27,9.21s4.39,8.53,9.21,8.27Z"/><path fill="#f4980c" fill-rule="evenodd" d="m294.88,28.72c-.89-16.7-15.21-29.57-31.91-28.67-7.72.41-14.6,3.71-19.71,8.77-2.29,2.27-15.27,18.22-15.27,18.22l2,1.78c2.17,5.08,1.38,11.17-2.54,15.56-4.03,4.53-10.24,5.98-15.65,4.17l9.46-10.63-8.65-7.7-9.46,10.62c-2.42-5.16-1.7-11.5,2.34-16.02,3.91-4.4,9.87-5.88,15.16-4.32l2.13,1.9,14.59-17.38c-5.23-3.44-11.53-5.33-18.25-4.98-16.7.89-29.56,15.21-28.67,31.91s15.21,29.57,31.91,28.67c8.76-.47,16.43-4.64,21.65-10.9,1.49-1.73,13.93-16.55,13.93-16.55,0,0-1.08-1.87-1.4-2.85h0s-3.28.18-3.28.18l-.37-6.94,3.28-.18h0c.23-1.07.6-2.09,1.08-3.05h0s-2.44-2.19-2.44-2.19l4.65-5.17,2.44,2.2h0c.9-.58,1.88-1.05,2.92-1.39h0s-.18-3.28-.18-3.28l6.94-.37.18,3.28h0c1.07.23,2.1.6,3.06,1.08h0s2.2-2.45,2.2-2.45l5.17,4.65-2.2,2.44h0c.58.9,1.05,1.88,1.4,2.92h0s3.28-.18,3.28-.18l.37,6.94-3.28.18h0c-.23,1.07-.6,2.09-1.08,3.05h0s2.44,2.19,2.44,2.19l-4.65,5.17-2.44-2.2h0c-.9.58-1.88,1.05-2.92,1.4h0s.18,3.28.18,3.28l-6.94.37-.18-3.28h0c-1.05-.23-3.06-1.1-3.06-1.1l-15,17.88c5.2,3.38,11.45,5.24,18.11,4.88,16.7-.89,29.56-15.21,28.67-31.91Z"/></g></svg>`;

const showScanFeedback = (type, message) => {
  scanFeedback.value = { show: true, type, message };
  setTimeout(() => {
    scanFeedback.value.show = false;
  }, 3000);
};

// ========================================
// METHODS - Hilfsfunktionen
// ========================================

const fetchStatistik = async () => {
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'get_statistik' }
    });

    if (response.data.status === 'success') {
      statistik.value = response.data.statistik;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Statistik:', error);
  }
};

const fetchArtikelListe = async () => {
  loading.value.artikel = true;
  try {
    const response = await inventurClient.get(inventurApiUrl(), {
      params: { action: 'get_katalog' }
    });

    if (response.data.status === 'success') {
      artikelListe.value = response.data.katalog || [];
    }
  } catch (error) {
    console.error('Fehler beim Laden der Artikel:', error);
  } finally {
    loading.value.artikel = false;
  }
};

const formatDateTime = (dateTime) => {
  if (!dateTime) return '';
  const date = new Date(dateTime);
  return date.toLocaleString('de-DE');
};

const getStatusColor = (status) => {
  const colors = {
    erfassung: 'blue',
    abgeschlossen: 'green',
    archiv: 'grey'
  };
  return colors[status] || 'grey';
};

const getStatusText = (status) => {
  const texts = {
    erfassung: 'Erfassung',
    abgeschlossen: 'Abgeschlossen',
    archiv: 'Archiv'
  };
  return texts[status] || status;
};

// ========================================
// LIFECYCLE
// ========================================

onMounted(() => {
  fetchPaletten();
  fetchStatistik();
  fetchArtikelListe();
});
</script>

<style scoped>
.inventur-container {
  padding: 20px;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.overview-card {
  background: rgba(30, 30, 40, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
}

.info-box {
  background: rgba(255, 255, 255, 0.05);
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.info-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.dialog-card {
  background-color: rgba(30, 30, 35, 0.9);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
}

code {
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}
</style>
